import { useMemo } from 'react';
import { useAtom } from 'jotai';
import { Draggable, Edit, Close } from '@carbon/react/icons';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { colorRulesAtom } from './atoms';
import { countAttrValue, type ColorRule } from './color-by';
import { format0 } from 'utils/number';
import type { ComparisonResult } from './types';
import style from './comparison-sidebar.module.scss';

const DND_MODIFIERS = [restrictToVerticalAxis];

interface ColorRuleCardProps {
  rule: ColorRule;
  results: ComparisonResult[];
  onEdit: (rule: ColorRule) => void;
  onRemove: (id: string) => void;
  onToggle: (id: string) => void;
  onColor: (ruleId: string, value: string, color: string) => void;
}

// One color rule: drag handle, label, enable/edit/remove controls, and a
// swatch + label + count per colored value.
const ColorRuleCard = ({
  rule,
  results,
  onEdit,
  onRemove,
  onToggle,
  onColor,
}: ColorRuleCardProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rule.id });

  return (
    <li
      ref={setNodeRef}
      className={style.ruleCard}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
      }}
    >
      <div className={style.ruleHeader}>
        <button
          type="button"
          className={style.dragHandle}
          aria-label={`Reorder ${rule.label}`}
          {...attributes}
          {...listeners}
        >
          <Draggable />
        </button>
        <span
          className={`${style.ruleTitle} ${
            rule.enabled ? '' : style.ruleDisabled
          }`}
        >
          {rule.label}
        </span>
        <button
          type="button"
          className={style.ruleAction}
          onClick={() => onToggle(rule.id)}
        >
          {rule.enabled ? 'Disable' : 'Enable'}
        </button>
        <button
          type="button"
          className={style.ruleIcon}
          aria-label="Edit rule"
          onClick={() => onEdit(rule)}
        >
          <Edit size={16} />
        </button>
        <button
          type="button"
          className={style.ruleIcon}
          aria-label="Remove color rule"
          onClick={() => onRemove(rule.id)}
        >
          <Close size={16} />
        </button>
      </div>
      <ul className={style.ruleValues}>
        {rule.values.map((v) => (
          <li key={v.value} className={style.ruleValue}>
            <input
              type="color"
              className={style.swatchInput}
              aria-label={`Pick color for ${v.label}`}
              value={v.color}
              onChange={(e) => onColor(rule.id, v.value, e.target.value)}
            />
            <span className={style.ruleValueLabel}>{v.label}</span>
            <span className={style.ruleValueCount}>
              ({format0(countAttrValue(results, rule.attribute, v.value))})
            </span>
          </li>
        ))}
      </ul>
    </li>
  );
};

interface ComparisonColorRulesProps {
  results: ComparisonResult[];
  onEdit: (rule: ColorRule) => void;
}

/**
 * The ordered, drag-to-reorder color rule list beneath "Color by Attribute" in
 * the sidebar. Each rule can be enabled/disabled, edited, removed, and recolored;
 * a property matching several rules takes the color of the rule listed first.
 * @param {ComparisonColorRulesProps} props - results (for counts) and an edit handler
 * @return {JSX.Element | null} The rule list, or null when there are no rules
 */
export const ComparisonColorRules = ({
  results,
  onEdit,
}: ComparisonColorRulesProps) => {
  const [rules, setRules] = useAtom(colorRulesAtom);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const ids = useMemo(() => rules.map((r) => r.id), [rules]);

  const handleReorder = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = rules.findIndex((r) => r.id === active.id);
    const to = rules.findIndex((r) => r.id === over.id);
    if (from === -1 || to === -1) return;
    setRules(arrayMove(rules, from, to));
  };

  const remove = (id: string) =>
    setRules((prev) => prev.filter((r) => r.id !== id));
  const toggle = (id: string) =>
    setRules((prev) =>
      prev.map((r) => (r.id === id ? { ...r, enabled: !r.enabled } : r))
    );
  const setColor = (ruleId: string, value: string, color: string) =>
    setRules((prev) =>
      prev.map((r) => {
        if (r.id !== ruleId) return r;
        return {
          ...r,
          values: r.values.map((v) => {
            if (v.value !== value) return v;
            return { ...v, color };
          }),
        };
      })
    );

  if (rules.length === 0) return null;

  return (
    <div className={style.rules}>
      {rules.length > 1 && (
        <p className={style.rulesHint}>
          Drag to reorder — properties that match several rules take the color
          of the rule listed first.
        </p>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={DND_MODIFIERS}
        onDragEnd={handleReorder}
      >
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <ul className={style.ruleList}>
            {rules.map((rule) => (
              <ColorRuleCard
                key={rule.id}
                rule={rule}
                results={results}
                onEdit={onEdit}
                onRemove={remove}
                onToggle={toggle}
                onColor={setColor}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
    </div>
  );
};
